import { html } from 'lit-html';

/**
 * Componente de fundo animado com bolhas coloridas em movimento
 * Segue o movimento do mouse e usa as cores customizáveis do usuário
 *
 * NOTA: Animação DESATIVADA para reduzir uso de GPU/memória
 * Use enableAnimation() para reativar se necessário
 */

// Flag para controlar se animação está ativa
// DESATIVADO por padrão para economizar GPU
let animationEnabled = false;

// Sistema de partículas/bolhas
class Bubble {
  constructor() {
    this.reset();
    this.vx = (Math.random() - 0.5) * 0.05; // Reduzido de 0.3 para 0.05 (6x mais lento)
    this.vy = (Math.random() - 0.5) * 0.05; // Reduzido de 0.3 para 0.05 (6x mais lento)
  }

  reset() {
    this.x = Math.random() * 100;
    this.y = Math.random() * 100;
    this.size = 200 + Math.random() * 400;
    this.baseSize = this.size;
    this.opacity = 0.10 + Math.random() * 0.10; // Reduzido de 0.25-0.45 para 0.10-0.20 (mais suave)
    this.color = Math.random() > 0.5 ? 'primary' : 'secondary';
    this.pulseSpeed = 0.0002 + Math.random() * 0.0005; // Reduzido de 0.001-0.003 para 0.0002-0.0007 (5x mais lento)
    this.pulseOffset = Math.random() * Math.PI * 2;
  }

  update(mouseX, mouseY, time) {
    // Movimento próprio aleatório
    this.x += this.vx;
    this.y += this.vy;

    // Atração pelo mouse (mais suave)
    const dx = mouseX - this.x;
    const dy = mouseY - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 30) {
      this.vx += dx * 0.00003; // Reduzido de 0.0002 para 0.00003 (atração mais suave)
      this.vy += dy * 0.00003; // Reduzido de 0.0002 para 0.00003 (atração mais suave)
    }

    // Limitar velocidade
    const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
    if (speed > 0.08) { // Reduzido de 0.5 para 0.08 (velocidade máxima muito menor)
      this.vx = (this.vx / speed) * 0.08;
      this.vy = (this.vy / speed) * 0.08;
    }

    // Bounce nas bordas
    if (this.x < -10 || this.x > 110) this.vx *= -0.8;
    if (this.y < -10 || this.y > 110) this.vy *= -0.8;

    // Manter dentro dos limites
    this.x = Math.max(-10, Math.min(110, this.x));
    this.y = Math.max(-10, Math.min(110, this.y));

    // Pulsação suave
    this.size = this.baseSize + Math.sin(time * this.pulseSpeed + this.pulseOffset) * 50;
  }
}

let bubbles = [];
let mouseX = 50;
let mouseY = 50;
let targetX = 50;
let targetY = 50;
let animationFrame = null;
let startTime = Date.now();

// Função para animar as bolhas
const animateGradient = () => {
  const time = Date.now() - startTime;

  // Interpolação suave do mouse (mais lenta)
  mouseX += (targetX - mouseX) * 0.01; // Reduzido de 0.05 para 0.01 (5x mais lento)
  mouseY += (targetY - mouseY) * 0.01; // Reduzido de 0.05 para 0.01 (5x mais lento)

  // Atualizar todas as bolhas
  bubbles.forEach(bubble => bubble.update(mouseX, mouseY, time));

  // Aplicar ao DOM
  const background = document.querySelector('.animated-background');
  if (background) {
    // Criar gradientes para cada bolha
    const gradients = bubbles.map(bubble => {
      const colorVar = bubble.color === 'primary' ? 'var(--primary)' : 'var(--secondary)';
      return `radial-gradient(circle ${bubble.size}px at ${bubble.x}% ${bubble.y}%, color-mix(in srgb, ${colorVar} ${bubble.opacity * 100}%, transparent) 0%, transparent 100%)`;
    });

    background.style.backgroundImage = gradients.join(', ');
  }

  animationFrame = requestAnimationFrame(animateGradient);
};

// Handler do movimento do mouse
const handleMouseMove = (e) => {
  targetX = (e.clientX / window.innerWidth) * 100;
  targetY = (e.clientY / window.innerHeight) * 100;
};

// Aplicar fundo estático (sem animação - economiza GPU)
const applyStaticBackground = () => {
  const background = document.querySelector('.animated-background');
  if (background) {
    // Gradiente estático suave usando as cores do tema
    background.style.backgroundImage = `
      radial-gradient(circle 600px at 20% 30%, color-mix(in srgb, var(--primary) 8%, transparent) 0%, transparent 100%),
      radial-gradient(circle 500px at 80% 70%, color-mix(in srgb, var(--secondary) 6%, transparent) 0%, transparent 100%),
      radial-gradient(circle 400px at 50% 50%, color-mix(in srgb, var(--primary) 4%, transparent) 0%, transparent 100%)
    `;
  }
};

// Inicializar listeners
const initializeBackground = () => {
  // Se animação desativada, usar fundo estático
  if (!animationEnabled) {
    applyStaticBackground();
    return;
  }

  const isMobile = window.innerWidth <= 768;

  // Criar bolhas (quantidade reduzida em mobile para performance)
  const bubbleCount = isMobile ? 3 : 8; // 3 bolhas em mobile, 8 em desktop
  bubbles = [];
  for (let i = 0; i < bubbleCount; i++) {
    bubbles.push(new Bubble());
  }

  // Em mobile, não adicionar listener de mouse para economizar recursos
  if (!isMobile) {
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
  }

  // Iniciar animação
  if (!animationFrame) {
    startTime = Date.now();
    animateGradient();
  }

};

// Limpar listeners (para cleanup)
const cleanupBackground = () => {
  document.removeEventListener('mousemove', handleMouseMove);
  if (animationFrame) {
    cancelAnimationFrame(animationFrame);
    animationFrame = null;
  }
  bubbles = [];
};

// Funções para controlar animação em runtime
export const enableAnimation = () => {
  animationEnabled = true;
  cleanupBackground();
  initializeBackground();
};

export const disableAnimation = () => {
  animationEnabled = false;
  cleanupBackground();
  applyStaticBackground();
};

export const isAnimationEnabled = () => animationEnabled;

// Exportar função de inicialização
export const initAnimatedBackground = () => {
  // Aguardar um frame para garantir que o DOM está pronto
  requestAnimationFrame(() => {
    initializeBackground();
  });
};

// Exportar função de cleanup
export const cleanupAnimatedBackground = cleanupBackground;

// Componente de fundo animado
export const animatedBackground = () => {
  // Inicializar após renderização
  setTimeout(initAnimatedBackground, 0);

  return html`
    <div class="animated-background"></div>

    <style>
      .animated-background {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        /* Usar altura dinâmica do viewport para mobile */
        height: 100vh;
        height: 100dvh; /* Dynamic viewport height - se suportado */
        height: var(--app-height, 100vh); /* Fallback com JS */
        z-index: -1;
        pointer-events: none;
        overflow: hidden;

        /* Obter cores customizáveis do usuário */
        --primary: var(--primary-color, #1877F2);
        --secondary: var(--secondary-color, #42B883);

        /* Fundo base */
        background: #ffffff;

        /* As bolhas serão aplicadas via JavaScript */
        background-image: none;

        /* Transição suave */
        transition: background-color 0.3s ease;
      }

      /* Adicionar textura de ruído sutil */
      .animated-background::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0.08;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
        pointer-events: none;
        mix-blend-mode: overlay;
      }

      /* Modo escuro */
      [data-theme="dark"] .animated-background {
        background: #1a1a1a;
      }

      /* Landing page sempre com fundo claro */
      .landing-page ~ .animated-background,
      .landing-page .animated-background,
      body:has(.landing-page) .animated-background {
        background: #ffffff !important;
      }

      /* Performance: usar will-change para otimização */
      .animated-background {
        will-change: background-image;
      }

      /* Reduzir movimento para usuários com preferência de movimento reduzido */
      @media (prefers-reduced-motion: reduce) {
        .animated-background {
          /* Desabilitar animação se o usuário prefere movimento reduzido */
          background-image: radial-gradient(
            circle 600px at 50% 50%,
            color-mix(in srgb, var(--primary) 10%, transparent) 0%,
            transparent 50%
          ) !important;
        }
      }

      /* Otimizar animação em mobile para performance */
      @media (max-width: 768px) {
        /* Remover textura de ruído em mobile */
        .animated-background::after {
          display: none;
        }
      }
    </style>
  `;
};
